import { computed, onMounted } from 'vue'
import useGet from '../../../core/composables/api/useGet'
import type { Member } from '../../../core/models/member/member'
import { CoachingStatus } from '../../dashboard/composables/useCoachingInvitations'
import { EventKey } from '../../../core/events/event-key'
import { useEventBus } from '@vueuse/core'
import type { DialogLeadTimeOption, DialogPhase } from '@/core/models/dialog/dialog'

export const useAllCoachees = () => {
  const {
    data: coacheesOverview,
    load,
    status,
    reload,
  } = useGet<CoacheesOverview>('coaching/overview')

  onMounted(() => {
    load()
  })
  const feedbackTopicTaskUpdatedBus = useEventBus(EventKey.feedbackTopicTaskUpdated)
  feedbackTopicTaskUpdatedBus.on(() => {
    reload()
  })

  const coachingDeletedBus = useEventBus(EventKey.coachingDeleted)
  const coachingAddedBus = useEventBus(EventKey.coachingAdded)
  const coachingsAddedBus = useEventBus(EventKey.coachingsAdded)

  coachingDeletedBus.on(() => {
    reload()
  })

  coachingAddedBus.on(() => {
    reload()
  })

  coachingsAddedBus.on(() => {
    reload()
  })

  const rejectedCoachings = computed(() => {
    return coacheesOverview.value?.coachees.filter(
      (coachee) => coachee.coachingStatus === CoachingStatus.Rejected,
    )
  })

  const activeCoachees = computed(() => {
    return coacheesOverview.value?.coachees.filter(
      (coachee) => coachee.coachingStatus === CoachingStatus.Active,
    )
  })

  const pendingCoachees = computed(() => {
    return coacheesOverview.value?.coachees.filter(
      (coachee) => coachee.coachingStatus === CoachingStatus.Pending,
    )
  })

  const allCoachees = computed(() => {
    return coacheesOverview.value?.coachees
  })

  return {
    status,
    activeCoachees,
    rejectedCoachings,
    allCoachees,
    pendingCoachees,
  }
}

export interface CoacheesOverview {
  readonly coachees: ReadonlyArray<CoacheeOverviewItem>
}

interface CoacheeOverviewItem {
  readonly coachee: Member
  readonly coachingStatus: CoachingStatus
  readonly feedbackGoals: ReadonlyArray<CoacheeGoalProgress>
  readonly activeSurveys: ReadonlyArray<CoacheeActiveSurvey>
  readonly currentDialog?: CoacheeCurrentDialog
}

interface CoacheeGoalProgress {
  readonly id: string
  readonly feedbackTopic: { id: string; title: string; icon?: string }
  readonly isActive: boolean
  readonly progress: ReadonlyArray<{
    readonly perspectiveKey: string
    readonly count: number
    readonly target: number
  }>
}

interface CoacheeActiveSurvey {
  readonly id: string
  readonly title?: string
  readonly feedbackTopic: { id: string; title: string; icon?: string }
  readonly selfFeedbackDone: boolean
  readonly totalAnsweredOccurrences: number
}

interface CoacheeCurrentDialog {
  readonly id: string
  readonly phase: DialogPhase
  readonly createdAt: string
  readonly plannedAt?: string
  readonly coacheeDataReleasedAt?: string
  readonly coachDataReleasedAt?: string
  readonly leadTimeStartsAt?: string
  readonly leadTimeOption?: DialogLeadTimeOption
}
